import { Header } from '@workspace/shared/components/Header';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { company } from '~/company/Company';
import { HeaderDubaiExpress } from '~/components/HeaderDubaiExpress';
import { HeaderAddressButton } from '~/components/mcosmetic/header/HeaderAddressButton';
import { HeaderMainShopCategoresList } from '~/components/mcosmetic/header/HeaderMainShopCategoresList';
import { HeaderNavigation } from '~/components/mcosmetic/header/HeaderNavigation';
import { useDateLang } from '~/hooks/useDateLang';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { orderStore, userStore } from '~/stores';
import { getAddressModalParams } from '~/utils/addressModal';

import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '../../hooks/useModal';

import { HeaderSearch } from '../HeaderSearch';
import { HeaderV3 } from '../HeaderV3';

import { DeliveryButton } from './components/DeliveryButton';
import { DownloadApp } from './components/DownloadApp';
import HeaderMenu from './components/HeaderMenu';
import { NavButtons } from './components/NavButtons';
import { DESKTOP_HEADER_ID } from './constants';
import styles from './Header.module.scss';

type HeaderProps = Omit<
  React.ComponentPropsWithRef<'header'>,
  'children' | 'id'
>;

const SaasHeader = React.forwardRef<HTMLHeadingElement, HeaderProps>(
  ({ className, ...props }, ref) => {
    const { openModal } = useModal();
    const { timeFormat } = useTimeFormat();
    const { locale } = useDateLang();
    const { pathname } = useLocation();

    const isHomePage = pathname === '/';

    const handleDeliveryAddress = () => {
      const [targetModal, source, params] = getAddressModalParams('header', {
        [MODAL_EDIT_ADDRESS_PARAM]: userStore?.deliveryAddress?.addressId || '',
      });

      openModal(targetModal, source, params);
    };

    // Use redesigned header for CityDrinks
    if (company.newDesignSystemEnabled) {
      return company.match({
        mcosmetic: (
          <Header.MCosmetic
            {...props}
            logo={{
              mobile: company.images.logoMobile?.vector,
              desktop: company.images.logo.vector,
            }}
            logoWrapper={({ children }) => <Link to="/">{children}</Link>}
            ref={ref}
            className={className}
            shouldChangeColorOnScroll={isHomePage}
          >
            <Header.Address>
              <HeaderAddressButton onClick={handleDeliveryAddress} />
            </Header.Address>
            <Header.MainShopCategories>
              <HeaderMainShopCategoresList />
            </Header.MainShopCategories>
            <Header.Navigation>
              <HeaderNavigation />
            </Header.Navigation>
          </Header.MCosmetic>
        ),
        dubaiexpress: (
          <HeaderDubaiExpress {...props} ref={ref} className={className} />
        ),
        default: <HeaderV3 {...props} ref={ref} className={className} />,
      });
    }

    const {
      message: expressDeliveryMessage,
      args: expressDeliveryMessageArgs,
      showFlashIcon,
    } = orderStore.getDeliveryLabel({ timeFormat, locale });

    return (
      <header
        {...props}
        ref={ref}
        id={DESKTOP_HEADER_ID}
        className={classes([styles.header, styles.header_root, className])}
        data-company={company.name}
      >
        <DownloadApp />
        <div className={classes(['content-layout', styles.contentLayout])}>
          <div className={styles.header_top}>
            <Link data-link="header-logo" to="/">
              <img
                src={company.images.logo.dark}
                alt=""
                className={styles.header_logo}
              />
            </Link>
            <HeaderSearch />
            <DeliveryButton
              {...{
                expressDeliveryMessage,
                expressDeliveryMessageArgs,
                showFlashIcon,
                handleDeliveryAddress,
              }}
            />
            <NavButtons />
          </div>
        </div>
        <HeaderMenu />
      </header>
    );
  },
);
SaasHeader.displayName = 'Header';

export default observer(SaasHeader);
